import React from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch } from 'react-redux';
import { Menu, MenuItems, MenuButton, MenuItem } from '@headlessui/react';
import {
  UserIcon,
  BellIcon,
  CalendarIcon,
  CreditCardIcon,
  BanknotesIcon,
  AdjustmentsHorizontalIcon,
  ShieldCheckIcon,
  QuestionMarkCircleIcon,
  ArrowRightOnRectangleIcon,
  ChevronDownIcon,
} from '@heroicons/react/24/outline';

import { logout } from '../../../redux/actions/auth';

const ICONS = {
  info: UserIcon,
  notifications: BellIcon,
  myEvents: CalendarIcon,
  paymentDetails: CreditCardIcon,
  financials: BanknotesIcon,
  preferences: AdjustmentsHorizontalIcon,
  securityPrivacy: ShieldCheckIcon,
  help: QuestionMarkCircleIcon,
};

const initialsOf = (str) => {
  if (!str || typeof str !== 'string') return '?';
  const parts = str.trim().split(/\s+/).slice(0, 2);
  return parts.map((p) => p.charAt(0).toUpperCase()).join('') || '?';
};

const Sidebar = ({
  user,
  info,
  onSectionChange,
  activeSection,
  notificationCount,
  paymentDetailsAlert = false,
}) => {
  const { t } = useTranslation();
  const dispatch = useDispatch();

  const navigation = [
    { name: t('dashboard.sidebar.main_info'), section: 'info' },
    { name: t('dashboard.sidebar.notifications'), section: 'notifications' },
    {
      name:
        user === 'promoter'
          ? t('dashboard.sidebar.my_events')
          : t('dashboard.sidebar.my_bookings'),
      section: 'myEvents',
    },
    { name: t('dashboard.sidebar.payment_details'), section: 'paymentDetails' },
    // Promoter-only: a brand has no repass to follow, and the endpoint behind
    // this tab is scoped to promoters.
    ...(user === 'promoter'
      ? [{ name: t('dashboard.sidebar.financials'), section: 'financials' }]
      : []),
    { name: t('dashboard.sidebar.preferences'), section: 'preferences' },
    {
      name: t('dashboard.sidebar.security_privacy'),
      section: 'securityPrivacy',
    },
    { name: t('dashboard.sidebar.help'), section: 'help' },
  ];

  const currentItem =
    navigation.find((item) => item.section === activeSection) || navigation[0];

  const displayName = info?.username || info?.companyName || '';
  const region = [info?.city, info?.country].filter(Boolean).join(', ');
  const initials = initialsOf(displayName);

  const handleLogout = () => dispatch(logout());

  return (
    <div className="w-full">
      {/* Mobile Dropdown */}
      <div className="block lg:hidden mb-3">
        <Menu as="div" className="relative inline-block w-full text-left">
          <MenuButton className="w-full inline-flex justify-between items-center gap-3 px-4 py-3 text-sm font-bold text-ink bg-white border border-line rounded-2xl shadow-softer hover:bg-paper-2">
            <span className="flex items-center gap-3 min-w-0">
              {info?.avatar ? (
                <img
                  src={info.avatar}
                  alt=""
                  className="w-8 h-8 rounded-full object-cover shrink-0"
                />
              ) : (
                <span className="w-8 h-8 rounded-full bg-brand text-white flex items-center justify-center text-[12px] font-bold shrink-0">
                  {initials}
                </span>
              )}
              <span className="truncate">{currentItem.name}</span>
            </span>
            <ChevronDownIcon className="w-5 h-5 text-muted shrink-0" />
          </MenuButton>
          <MenuItems className="absolute z-20 mt-2 w-full bg-white border border-line rounded-2xl shadow-card focus:outline-none overflow-hidden">
            {navigation.map((item) => {
              const Icon = ICONS[item.section] || UserIcon;
              const isActive = activeSection === item.section;
              return (
                <MenuItem key={item.section}>
                  {({ active }) => (
                    <button
                      onClick={() => onSectionChange(item.section)}
                      className={`w-full flex items-center gap-3 text-left px-4 py-3 text-sm font-medium ${
                        isActive
                          ? 'bg-ink text-white'
                          : active
                          ? 'bg-paper-2 text-ink'
                          : 'text-ink/80'
                      }`}
                    >
                      <Icon className="w-[18px] h-[18px] shrink-0" />
                      <span className="flex-1 truncate">{item.name}</span>
                      {item.section === 'notifications' &&
                        notificationCount > 0 && (
                          <span
                            className={`text-[11px] font-bold rounded-full px-2 py-0.5 ${
                              isActive
                                ? 'bg-white/20 text-white'
                                : 'bg-rose-500 text-white'
                            }`}
                          >
                            {notificationCount}
                          </span>
                        )}
                      {item.section === 'paymentDetails' &&
                        paymentDetailsAlert && (
                          <span
                            aria-label={t('dashboard.sidebar.setup_pending')}
                            title={t('dashboard.sidebar.setup_pending')}
                            className={`w-1.5 h-1.5 rounded-full shrink-0 ${
                              isActive ? 'bg-white/70' : 'bg-amber-500'
                            }`}
                          />
                        )}
                    </button>
                  )}
                </MenuItem>
              );
            })}
            <MenuItem>
              {({ active }) => (
                <button
                  onClick={handleLogout}
                  className={`w-full flex items-center gap-3 text-left px-4 py-3 text-sm font-medium border-t border-line text-muted ${
                    active ? 'bg-paper-2 text-ink' : ''
                  }`}
                >
                  <ArrowRightOnRectangleIcon className="w-[18px] h-[18px] shrink-0" />
                  <span>{t('dashboard.logout')}</span>
                </button>
              )}
            </MenuItem>
          </MenuItems>
        </Menu>
      </div>

      {/* Desktop Sidebar */}
      <aside className="hidden lg:flex w-[288px] shrink-0 bg-white text-ink flex-col rounded-3xl shadow-card border border-line/60 overflow-hidden">
        {/* Account card */}
        <div className="p-5">
          <div className="flex items-center gap-3">
            <div className="relative shrink-0">
              {info?.avatar ? (
                <img
                  src={info.avatar}
                  alt=""
                  className="w-11 h-11 rounded-full object-cover"
                />
              ) : (
                <div className="w-11 h-11 rounded-full bg-brand text-white flex items-center justify-center text-[14px] font-bold">
                  {initials}
                </div>
              )}
            </div>
            <div className="min-w-0">
              <div
                className="text-[14.5px] font-bold text-ink-strong truncate leading-tight"
                title={displayName || ''}
              >
                {displayName || '—'}
              </div>
              {region && (
                <div className="text-[11.5px] text-muted truncate mt-0.5">
                  {region}
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Nav */}
        <nav className="flex-1 px-3 pb-3">
          <ul className="space-y-1.5">
            {navigation.map((item) => {
              const Icon = ICONS[item.section] || UserIcon;
              const isActive = item.section === activeSection;
              return (
                <li key={item.section}>
                  <button
                    onClick={() => onSectionChange(item.section)}
                    className={`w-full flex items-center gap-3 px-4 h-11 rounded-l-3xl rounded-r-lg text-left transition-colors
                      ${
                        isActive
                          ? 'bg-ink-strong text-white shadow-md'
                          : 'bg-paper text-muted hover:text-ink hover:bg-paper-2'
                      }`}
                  >
                    <Icon className="w-[18px] h-[18px] shrink-0" />
                    <span
                      className={`text-[13px] font-bold truncate flex-1 ${
                        isActive ? 'text-white' : ''
                      }`}
                    >
                      {item.name}
                    </span>
                    {item.section === 'notifications' &&
                      notificationCount > 0 && (
                        <span
                          className={`text-[10.5px] font-bold rounded-full px-1.5 h-[18px] inline-flex items-center ${
                            isActive
                              ? 'bg-white/25 text-white'
                              : 'bg-rose-500 text-white'
                          }`}
                        >
                          {notificationCount}
                        </span>
                      )}
                    {item.section === 'paymentDetails' &&
                      paymentDetailsAlert && (
                        <span
                          aria-label={t('dashboard.sidebar.setup_pending')}
                          title={t('dashboard.sidebar.setup_pending')}
                          className={`w-1.5 h-1.5 rounded-full shrink-0 ${
                            isActive ? 'bg-white/70' : 'bg-amber-500'
                          }`}
                        />
                      )}
                  </button>
                </li>
              );
            })}
          </ul>
        </nav>

        {/* Footer */}
        <div className="p-3 border-t border-line/70">
          <button
            onClick={handleLogout}
            className="w-full flex items-center gap-3 px-3 h-9 text-muted hover:text-ink-strong text-[12.5px] font-bold rounded-lg hover:bg-paper transition-colors"
          >
            <ArrowRightOnRectangleIcon className="w-[15px] h-[15px]" />
            <span>{t('dashboard.logout')}</span>
          </button>
        </div>
      </aside>
    </div>
  );
};

export default Sidebar;
